import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { SitePlanCanvas } from "@/components/site-plan/SitePlanCanvas";
import type { PitchHighlightRole } from "@/lib/sitePlan";
import type { SitePlanSettings, SitePlanStatusResponse } from "@/lib/sitePlan";

interface CheckInPitchSummaryMapProps {
  pitchId: string;
  referenceDate: string;
}

export function CheckInPitchSummaryMap({ pitchId, referenceDate }: CheckInPitchSummaryMapProps) {
  const [planImage, setPlanImage] = useState<string | null>(null);
  const [status, setStatus] = useState<SitePlanStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settings, planStatus] = await Promise.all([
        api.get<SitePlanSettings>("/api/v1/settings/site-plan?include_image=true"),
        api.get<SitePlanStatusResponse>(
          `/api/v1/site-plan/status?reference_date=${encodeURIComponent(referenceDate)}`,
        ),
      ]);
      setPlanImage(settings.image_data);
      setStatus(planStatus);
    } finally {
      setLoading(false);
    }
  }, [referenceDate]);

  useEffect(() => {
    void load();
  }, [load]);

  const pitchHighlights = useMemo(() => {
    const map: Record<string, PitchHighlightRole> = {};
    if (!status) return map;
    for (const pitch of status.pitches) {
      if (!pitch.map_polygon || pitch.map_polygon.length < 3) continue;
      map[pitch.pitch_id] = pitch.pitch_id === pitchId ? "assigned" : "dimmed";
    }
    return map;
  }, [status, pitchId]);

  const overlays = useMemo(() => {
    if (!status) return [];
    return status.pitches
      .filter((p) => p.map_polygon && p.map_polygon.length >= 3)
      .map((p) => ({
        id: p.pitch_id,
        code: p.code,
        map_polygon: p.map_polygon,
        status_kind: p.status_kind,
        transition_hint: p.transition_hint,
        booking_status: p.booking_status,
      }));
  }, [status]);

  if (loading) {
    return <p className="text-sm text-label-secondary">Platzplan wird geladen…</p>;
  }

  if (!planImage || overlays.length === 0) {
    return null;
  }

  return (
    <SitePlanCanvas
      imageData={planImage}
      pitches={overlays}
      badgeScale={status ? Number(status.badge_scale) || 1 : 1}
      mode="view"
      selectedPitchId={pitchId}
      pitchHighlights={pitchHighlights}
      readOnly
    />
  );
}
